import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import logoImage from "../../../assets/images/Logo timbo transparente colorido.png";
import assinaturaImage from "../../../assets/assinaturas/ASSINATURA.png";

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontFamily: "Times-Roman",
    fontSize: 11,
    flexDirection: "column",
    position: "relative",
  },
  header: {
    alignItems: "center",
    marginBottom: 15,
  },
  logo: {
    height: 60,
    width: "auto",
  },
  title: {
    fontSize: 14,
    textAlign: "center",
    textDecoration: "underline",
    fontWeight: "bold",
    fontFamily: "Times-Bold",
    marginBottom: 10,
  },
  solicitante: {
    fontSize: 12,
    marginBottom: 10,
    lineHeight: 1.2,
  },
  referencia: {
    fontSize: 11,
    fontFamily: "Times-Bold",
    textDecoration: "underline",
    textTransform: "uppercase",
    marginBottom: 10,
  },
  // Tabela
  table: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    marginBottom: 15,
  },
  tableRow: {
    flexDirection: "row",
  },
  tableHeaderRow: {
    backgroundColor: "#000000",
    color: "#ffffff",
  },
  tableCellHeader: {
    paddingVertical: 4,
    paddingHorizontal: 4,
    borderWidth: 1,
    borderColor: "#000000",
    textAlign: "center",
    fontFamily: "Times-Bold",
    fontSize: 10,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  tableCell: {
    paddingVertical: 4,
    paddingHorizontal: 4,
    borderWidth: 1,
    borderColor: "#000000",
    borderTopWidth: 0,
    fontSize: 10,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
  },
  tableCellDescricao: {
    textTransform: "uppercase",
    justifyContent: "center",
    alignItems: "flex-start",
    textAlign: "left",
  },
  tableCellTotal: {
    backgroundColor: "#000000",
    color: "#ffffff",
    fontFamily: "Times-Bold",
  },
  // Colunas
  colItem: { width: "10%" },
  colDesc: { width: "45%" },
  colQtd: { width: "10%" },
  colVal: { width: "15%" },
  colTotal: { width: "20%" },

  infoBlock: {
    marginBottom: 5,
    lineHeight: 1.2,
  },
  boldText: {
    fontFamily: "Times-Bold",
  },
  redText: {
    color: "#dc2626",
  },
  underlineText: {
    textDecoration: "underline",
  },
  listItem: {
    flexDirection: "row",
    marginBottom: 2,
    paddingLeft: 10,
  },
  bullet: {
    marginRight: 6,
  },
  signatureBlock: {
    marginTop: 20,
    marginBottom: 10,
  },
  signatureDate: {
    textAlign: "right",
    fontFamily: "Times-Bold",
    marginBottom: 5,
  },
  signatureImageWrapper: {
    alignItems: "center",
  },
  signatureImage: {
    height: 120,
    width: "auto",
  },
  footer: {
    position: "absolute",
    bottom: 30,
    left: 30,
    right: 30,
    fontSize: 10,
  },
});

const formatCurrency = (value) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
};

// Obter data atual formatada
const formatCurrentDate = () => {
  const dataAtual = new Date();
  const meses = [
    "Janeiro",
    "Fevereiro",
    "Março",
    "Abril",
    "Maio",
    "Junho",
    "Julho",
    "Agosto",
    "Setembro",
    "Outubro",
    "Novembro",
    "Dezembro",
  ];
  return `${dataAtual.getDate()} de ${meses[dataAtual.getMonth()]} de ${dataAtual.getFullYear()}`;
};

// Formatar data de validade
const formatValidDate = (dateString) => {
  if (!dateString) return "";
  const [year, month, day] = dateString.split("-");
  return `${day}/${month}/${year}`;
};

const getReferenceText = (tipoPacote, solicitouSobremesa) => {
  const txtSobremesa = solicitouSobremesa ? " + SOBREMESA" : "";
  switch (tipoPacote) {
    case "1":
      return `REF.: DIA DE LAZER, INCLUINDO ENTRADAS NOS DOIS PARQUEs AQUÁTICOS${txtSobremesa}.`;
    case "2":
      return `REF.: DIA DE LAZER, INCLUINDO ENTRADAS NOS DOIS PARQUEs AQUÁTICOS + ALMOÇO + REFRI OU SUCO OU ÁGUA${txtSobremesa}.`;
    case "3":
      return `REF.: DIA DE LAZER, INCLUINDO ENTRADAS NOS DOIS PARQUEs AQUÁTICOS + ALMOÇO${txtSobremesa}.`;
    default:
      return `REF.: DIA DE LAZER, INCLUINDO ENTRADAS NOS DOIS PARQUEs AQUÁTICOS + ALMOÇO + REFRI OU SUCO OU ÁGUA${txtSobremesa}.`;
  }
};

const calculateTotal = (itens) => {
  return itens.reduce(
    (acc, item) => acc + Number(item.qtd) * Number(item.valorUnitario || 0),
    0,
  );
};

const ReactPdfOrcamento = ({ data }) => {
  if (!data || !data.itens) return null;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Cabeçalho */}
        <View style={styles.header}>
          <Image src={logoImage} style={styles.logo} />
        </View>

        {/* Título */}
        <Text style={styles.title}>ORÇAMENTO</Text>

        {/* Dados do Solicitante */}
        <View style={styles.solicitante}>
          <Text>{data.empresa}</Text>
          {data.cnpj && <Text>CNPJ: {data.cnpj}</Text>}
          {data.endereco && <Text>Endereço: {data.endereco}</Text>}
        </View>

        {/* Referência */}
        <Text style={styles.referencia}>
          {getReferenceText(data.tipoPacote, data.solicitouSobremesa)}
        </Text>

        {/* Tabela */}
        <View style={styles.table}>
          {/* Header */}
          <View style={[styles.tableRow, styles.tableHeaderRow]}>
            <Text style={[styles.tableCellHeader, styles.colItem]}>ITEM</Text>
            <Text style={[styles.tableCellHeader, styles.colDesc]}>
              DESCRIÇÃO
            </Text>
            <Text style={[styles.tableCellHeader, styles.colQtd]}>QTD</Text>
            <Text style={[styles.tableCellHeader, styles.colVal]}>
              VALOR UNIT.
            </Text>
            <Text style={[styles.tableCellHeader, styles.colTotal]}>TOTAL</Text>
          </View>

          {/* Rows */}
          {data.itens.map((item, index) => {
            const totalItem = Number(item.qtd) * Number(item.valorUnitario);
            return (
              <View style={styles.tableRow} key={index}>
                <Text style={[styles.tableCell, styles.colItem]}>
                  {String(index + 1).padStart(2, "0")}
                </Text>
                <Text
                  style={[
                    styles.tableCell,
                    styles.tableCellDescricao,
                    styles.colDesc,
                  ]}
                >
                  {String(item.descricao).toUpperCase()}
                </Text>
                <Text style={[styles.tableCell, styles.colQtd]}>
                  {item.qtd}
                </Text>
                <Text style={[styles.tableCell, styles.colVal]}>
                  {formatCurrency(item.valorUnitario)}
                </Text>
                <Text style={[styles.tableCell, styles.colTotal]}>
                  {formatCurrency(totalItem)}
                </Text>
              </View>
            );
          })}

          {/* Total Footer */}
          <View style={styles.tableRow}>
            {/* Célula vazia combinada para as primeiras 4 colunas */}
            <View style={{ width: "80%" }}></View>
            <Text
              style={[styles.tableCell, styles.colTotal, styles.tableCellTotal]}
            >
              {formatCurrency(calculateTotal(data.itens))}
            </Text>
          </View>
        </View>

        {/* Informações Finais */}
        <View style={styles.infoBlock}>
          <Text
            style={[
              styles.boldText,
              { marginBottom: 10, textTransform: "uppercase" },
            ]}
          >
            ITENS VÁLIDOS ATÉ O DIA{" "}
            <Text style={styles.redText}>
              {formatValidDate(data.dataValidade)}
            </Text>
            .
          </Text>

          <Text
            style={[
              styles.boldText,
              { marginBottom: 10, textTransform: "uppercase" },
            ]}
          >
            AGENDAMENTO ANTECIPADO.
            {data.atendimentoSabado ? " ATENDIMENTO APENAS AOS SÁBADOS." : ""}
          </Text>

          <Text
            style={[
              styles.boldText,
              { marginBottom: 5, textTransform: "uppercase" },
            ]}
          >
            CONTA BANCÁRIA PARA DEPÓSITO,{" "}
            <Text style={[styles.redText, styles.underlineText]}>
              PAGAMENTO ANTECIPADO
            </Text>
            :
          </Text>

          <View>
            <View style={styles.listItem}>
              <Text style={styles.bullet}>-</Text>
              <Text>CAIXA ECONÔMICA FEDERAL</Text>
            </View>
            <View style={styles.listItem}>
              <Text style={styles.bullet}>-</Text>
              <Text>AGÊNCIA: 1733</Text>
            </View>
            <View style={styles.listItem}>
              <Text style={styles.bullet}>-</Text>
              <Text>CONTA Tipo 1292 nº 577547202-7</Text>
            </View>
            <View style={styles.listItem}>
              <Text style={styles.bullet}>-</Text>
              <Text>CNPJ: 08.855.628/0001-80</Text>
            </View>
            <View style={styles.listItem}>
              <Text style={styles.bullet}>-</Text>
              <Text>TIMBO TURISMO ECOLOGICO E RURAL LTDA - ME</Text>
            </View>
          </View>
        </View>

        {/* Assinatura */}
        <View style={styles.signatureBlock}>
          <Text style={styles.signatureDate}>
            SALGADO, {formatCurrentDate()}
          </Text>
          <View style={styles.signatureImageWrapper}>
            <Image src={assinaturaImage} style={styles.signatureImage} />
          </View>
        </View>

        {/* Rodapé Fixo */}
        <View style={styles.footer}>
          <Text>TIMBO TURISMO ECOLOGICO E RURAL LTDA – ME</Text>
          <Text>CNPJ: 08.855.628.0001-80</Text>
        </View>
      </Page>
    </Document>
  );
};

export default ReactPdfOrcamento;
